import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import { InterFont, InterFontBold } from '@C/Utils';
import TableToExcelExport from '@C/TableToExcelExport';
import { Pagination } from '@C/Utils';
import {
  generateLiveMemo,
  getBase64FromUrl,
  generateThermalMemo,
} from '@C/Utils/ExportUtils';
import { authService } from '@S';
import { Spinner } from '@C/Utils';

import './styles.scoped.scss';

const generateClassname = () => {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < 5; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

const _class = `tg-${generateClassname()}`;

const sanitizeWorksheetName = (name) => {
  return name.replace(/[*?:\/\\[\]]/g, '_');
};

const export2Excel = (exportOptions) => {
  let options = {};
  if (!exportOptions) {
    options = {
      name: `${authService.currentBunkName} - Report.xlsx`,
      sheet: {
        name: 'Sheet 1',
      },
    };
  } else {
    options = {
      name: `${authService.currentBunkName} - ${sanitizeWorksheetName(exportOptions.fileName)}.xlsx`,
      sheet: {
        name: `${sanitizeWorksheetName(exportOptions.reportName)}`,
      },
    };
  }
  TableToExcelExport.convert(document.getElementById(`${_class}`), options);
};

const export2PDF = async (exportOptions, resolve, reject, additionalTable) => {
  if (!exportOptions) {
    exportOptions = {
      fileName: `Report.pdf`,
      reportName: 'Report',
      reportDate: `Date - ${dayjs().format('D MMM YY')}`,
      reportTotal: '',
      reportSaleTotal: '',
      reportTCSTotal: '',
      reportQuantity: '',
      reportSales: '',
      reportTesting: '',
      reportBowser: '',
      reportCustomerName: '',
      reportGst: '',
      reportOpeningBalance: '',
      reportClosingBalance: '',
    };
  } else {
    exportOptions.fileName = `${exportOptions.fileName}.pdf`;
    exportOptions.reportName = `${exportOptions.reportName}`;
    exportOptions.reportCustomerName = `${exportOptions.reportCustomerName}`;
    exportOptions.reportDate = `${exportOptions.reportDate}`;
    exportOptions.reportTotal = `${exportOptions.reportTotal}`;
    exportOptions.reportSaleTotal = `${exportOptions.reportSaleTotal}`;
    exportOptions.reportTCSTotal = `${exportOptions.reportTCSTotal}`;
    exportOptions.reportQuantity = `${exportOptions.reportQuantity}`;
    exportOptions.reportSales = `${exportOptions.reportSales}`;
    exportOptions.reportTesting = `${exportOptions.reportTesting}`;
    exportOptions.reportBowser = `${exportOptions.reportBowser}`;
    exportOptions.reportOpeningBalance = `${exportOptions.reportOpeningBalance}`;
    exportOptions.reportClosingBalance = `${exportOptions.reportClosingBalance}`;
    exportOptions.reportGst = `${exportOptions.reportGst}`;
  }
  const doc = new jsPDF();
  doc.addFileToVFS('Inter-Regular.ttf', InterFont);
  doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
  doc.setFont('Inter');
  const bunkLogo = getBase64FromUrl(authService.currentOMCWebLogo);
  const bunkAddress = authService.currentBunkAddress;
  bunkLogo.then((result) => {
    const img = new Image();
    img.onload = function () {
      const aspectRatio = img.width / img.height;
      const width = 25;
      const height = width / aspectRatio;
      try {
        doc.addImage(result, 'PNG', 13, 10, width, height);
        autoTable(doc, {
          margin: { right: 5, left: 5 },
          body: [
            [
              {
                content: authService.currentBunkName,
                styles: {
                  halign: 'right',
                  fontSize: 15,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content:
                  bunkAddress.city +
                  ', ' +
                  bunkAddress.location +
                  ',' +
                  bunkAddress.country.country +
                  ' - ' +
                  bunkAddress.pincode,
                styles: {
                  halign: 'right',
                  fontSize: 12,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `GST: ${authService.currentUserGst}`,
                styles: {
                  halign: 'right',
                  fontSize: 10,
                  textColor: '#000000',
                },
              },
            ],
          ],
          theme: 'plain',
        });
        autoTable(doc, {
          margin: { right: 5, left: 5 },
          body: [
            [
              {
                content: exportOptions.reportName,
                styles: {
                  halign: 'left',
                  fontSize: 15,
                  textColor: '#000000',
                  fontStyle: 'bold',
                },
              },
              {
                content: exportOptions.reportDate,
                styles: {
                  halign: 'right',
                  fontSize: 12,
                  textColor: '#000000',
                  fontStyle: 'bold',
                },
              },
            ],
            [
              {
                content: exportOptions.reportCustomerName,
                styles: {
                  halign: 'left',
                  fontSize: 12,
                  textColor: '#000000',
                  fontStyle: 'bold',
                },
              },
            ],
            [
              {
                content: exportOptions.reportGst,
                styles: {
                  halign: 'left',
                  fontSize: 10,
                  textColor: '#000000',
                },
              },
            ],
          ],
          theme: 'plain',
        });
        autoTable(doc, {
          html: `.${_class}`,
          didParseCell: function (data) {
            // set table header color to black
            if (data.section === 'head') {
              data.cell.styles.fillColor = [0, 0, 0];
              data.cell.styles.textColor = [255, 255, 255];
            }
            const arr = data.cell.raw.classList;
            if ([...arr].includes('exclude-export')) {
              data.cell.text = '';
              data.cell.width = 0;
            }
          },
          theme: 'striped',
          styles: {
            overflow: 'linebreak',
            fontSize: 7,
            cellWidth: 'auto',
            font: 'Inter',
            fontStyle: 'normal',
          },
          margin: { top: 10, right: 5, bottom: 5, left: 5 },
        });

        if (
          additionalTable &&
          additionalTable.data &&
          additionalTable.columns
        ) {
          let additionalBody = [];
          additionalBody = additionalTable.data.map((item) =>
            additionalTable.columns.map((val) => item[val.key]),
          );
          autoTable(doc, {
            head: [additionalTable.columns.map((col) => col.name)],
            body: additionalBody,
            theme: 'striped',
            headStyles: {
              fillColor: [0, 0, 0],
              textColor: [255, 255, 255],
            },
            styles: {
              overflow: 'linebreak',
              fontSize: 7,
              cellWidth: 'auto',
              font: 'Inter',
              fontStyle: 'normal',
            },
            margin: { top: 10, right: 5, bottom: 5, left: 5 },
          });
        }
        if (exportOptions.reportOpeningBalance) {
          autoTable(doc, {
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportOpeningBalance,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportClosingBalance) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportClosingBalance,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportTotal) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportTotal,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportQuantity) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportQuantity,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportSales) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportSales,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportSaleTotal) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportSaleTotal,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportTCSTotal) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportTCSTotal,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportTesting) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportTesting,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        if (exportOptions.reportBowser) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 1,
            margin: { right: 5, left: 5 },
            body: [
              [
                {
                  content: exportOptions.reportBowser,
                  styles: {
                    halign: 'right',
                    fontSize: 9,
                    font: 'Inter',
                    textColor: '#000000',
                    fontStyle: 'bold',
                  },
                },
              ],
            ],
            theme: 'plain',
          });
        }
        doc.save(`${authService.currentBunkName} - ${exportOptions.fileName}`);
        resolve(true);
      } catch (e) {
        console.log(e);
        reject('Error while exporting to PDF');
      }
    };
    img.src = result;
  });
};

const formatCurrency = (value, decimalPlaces = 2) => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    style: 'decimal',
  }).format(value || 0);
};

const memoPdf = (memo) => {
  new Promise((resolve, reject) => {
    generateLiveMemo(memo, resolve, reject);
  })
    .then((response) => {
      toast.success('Memo generated successfully');
    })
    .catch((error) => {
      toast.error(error);
    });
};

const printMemo = (data) => {
  const memoText = generateThermalMemo(data);

  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Pop-up blocked. Please allow pop-ups.');
    return;
  }

  const doc = printWindow.document;

  doc.open();
  doc.write(`
    <html>
      <head>
        <title>Print Memo</title>
        <style>
          @page {
            size: 58mm auto; /* Set width for thermal printers */
            margin: 0;
          }

          body {
            margin: 0;
            padding: 0;
            width: 58mm; /* Ensure layout fits printer width */
          }

          pre {
            font-family: monospace;
            white-space: pre;
            font-size: 10px;
            line-height: 1.2;
            width: 58mm; /* Force wrap region to thermal width */
            margin: 0;
            padding: 5px;
          }
        </style>
      </head>
      <body></body>
    </html>
  `);
  doc.close();

  const pre = doc.createElement('pre');
  pre.textContent = memoText;
  doc.body.appendChild(pre);

  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    printWindow.onafterprint = () => printWindow.close();
  }, 200);
};

const TableGrid = ({ ...props }) => {
  const [dir, setDir] = useState(true);
  const [row, setRow] = useState(props.row);
  const [selectedList, setSelectedList] = useState([]);
  const enableCheckbox = props.enableCheckbox || false;
  const hideActions = props.hideActions ? true : false;
  const col = props.col;
  const enableExport = props.enableExport === false ? false : true;
  const [exportingPdf, setExportingPdf] = useState(false);
  const currentRole = authService.currentUserRole;
  const enablePagination = props.pagination || 'enabled';
  const tableSeparation = props.tableSeparation || null;
  const headerGroups = props.headerGroups || [];
  const headerGroupMap = new Map(
    headerGroups.map((group) => [group.key, group.name]),
  );
  const hasGroupedHeaders = col.some(
    (item) => item.group && headerGroupMap.has(item.group),
  );

  const groupedHeaderMeta = (() => {
    if (!hasGroupedHeaders) {
      return null;
    }

    const firstRow = [];
    const secondRow = [];
    let i = 0;

    while (i < col.length) {
      const current = col[i];
      const groupKey = current.group;

      if (groupKey && headerGroupMap.has(groupKey)) {
        let j = i;
        while (j < col.length && col[j].group === groupKey) {
          secondRow.push({ item: col[j], index: j });
          j += 1;
        }

        firstRow.push({
          type: 'group',
          key: groupKey,
          name: headerGroupMap.get(groupKey),
          span: j - i,
          startIndex: i,
        });
        i = j;
      } else {
        firstRow.push({
          type: 'single',
          item: current,
          index: i,
        });
        i += 1;
      }
    }

    return { firstRow, secondRow };
  })();

  const enabledActions = props.enabledActions || [
    {
      action: 'Edit',
      roles: ['Site Admin', 'Credit Customer', 'Dealer', 'Cashier', 'Manager'],
      callback: props.onEdit,
    },
    {
      action: 'Delete',
      roles: ['Site Admin', 'Credit Customer', 'Dealer', 'Cashier', 'Manager'],
      callback: props.onDelete,
    },
    {
      action: 'Pay',
      roles: ['Site Admin', 'Credit Customer', 'Dealer', 'Cashier', 'Manager'],
      callback: props.onPay,
    },
  ];

  useEffect(() => {
    if (enableCheckbox) {
      props.checkboxChange(selectedList);
    }
  }, [selectedList]);

  useEffect(() => {
    if (tableSeparation) {
      let rows = props.row;
      // Logic for separating table rows based on the provided key
      let filteredRows = props.row.filter(
        (item) => item[tableSeparation.key] === true,
      );
      let separatedRows = props.row.filter(
        (item) => item[tableSeparation.key] === false,
      );

      // add an empty row with label to indicate separation
      rows = [
        ...filteredRows,
        { isSeparator: true, label: tableSeparation.title },
        ...separatedRows,
      ];

      setRow(rows);
    } else {
      setRow(props.row);
    }
  }, [props.row]);

  useEffect(() => {
    if (!props.handlePagination || enablePagination !== 'enabled') {
      return;
    }

    const pageCount = Math.ceil((props.count || 0) / (props.limit || 20));
    const hasResults = (props.count || 0) > 0;
    const isOffsetOutOfRange = pageCount > 0 && props.offset > pageCount;
    const isEmptyNonFirstPage =
      hasResults && props.offset > 1 && row.length === 0;

    if (isOffsetOutOfRange || isEmptyNonFirstPage) {
      props.handlePagination(1);
    }
  }, [
    enablePagination,
    props.count,
    props.handlePagination,
    props.limit,
    props.offset,
    row.length,
  ]);

  const sortData = (id) => {
    const key = col[id].key;

    let arr = [];
    if (dir) {
      arr = [...row].sort((a, b) => {
        if (typeof a[key] === 'string' && typeof b[key] === 'string') {
          return a[key].localeCompare(b[key]);
        } else if (typeof a[key] === 'number' && typeof b[key] === 'number') {
          return a[key] - b[key];
        } else if (typeof a[key] === 'boolean' && typeof b[key] === 'boolean') {
          return a[key] === b[key] ? 0 : a[key] ? 1 : -1;
        } else if (a[key] instanceof Date && b[key] instanceof Date) {
          return a[key] - b[key];
        } else {
          // If the data type is not supported, return 0 to maintain the order
          return 0;
        }
      });
    } else {
      arr = [...row].sort((a, b) => {
        if (typeof a[key] === 'string' && typeof b[key] === 'string') {
          return b[key].localeCompare(a[key]);
        } else if (typeof a[key] === 'number' && typeof b[key] === 'number') {
          return b[key] - a[key];
        } else if (typeof a[key] === 'boolean' && typeof b[key] === 'boolean') {
          return a[key] === b[key] ? 0 : a[key] ? -1 : 1;
        } else if (a[key] instanceof Date && b[key] instanceof Date) {
          return b[key] - a[key];
        } else {
          // If the data type is not supported, return 0 to maintain the order
          return 0;
        }
      });
    }
    setRow(arr);
    setDir(!dir);
  };

  const generatePdf = (data, additionalTable = {}) => {
    setExportingPdf(true);
    new Promise((resolve, reject) => {
      export2PDF(data, resolve, reject, additionalTable);
    })
      .then(() => {
        setExportingPdf(false);
      })
      .catch((error) => {
        toast.error(error);
        setExportingPdf(false);
      });
  };

  return (
    <>
      <div className="tg-wrapper">
        <div className="tg-actions">
          <span>{`Total records: ${props.count || row.length}`}</span>
          {enableExport && (
            <div className="export-btns">
              <button
                className="blue-btn btn-with-icon btn-with-icon__excel"
                onClick={() => export2Excel(props.exportOptions)}
              >
                Export to Excel
              </button>
              <button
                className={`blue-btn btn-with-icon btn-with-icon__pdf ${
                  exportingPdf ? 'loading' : ''
                }`}
                onClick={() =>
                  generatePdf(props.exportOptions, props.additionalTable)
                }
              >
                Export to PDF
              </button>
            </div>
          )}
        </div>

        <table className={_class} id={_class}>
          <thead>
            {!hasGroupedHeaders && (
              <tr className="tg-header" data-header={true}>
                {enableCheckbox && (
                  <th className="tg-cell exclude-export text-center">
                    <span>Select</span>
                  </th>
                )}
                {col.map((item, i) => (
                  <th
                    key={`tg-head-${i}`}
                    className={
                      item.sortable != null && item.sortable
                        ? 'sortable tg-cell'
                        : 'tg-cell'
                    }
                    style={{
                      width: item.width || 'auto',
                      maxWidth: item.maxWidth || 'none',
                      minWidth: item.minWidth || 'auto',
                    }}
                  >
                    <span
                      onClick={() => {
                        item.sortable != null && item.sortable
                          ? sortData(i)
                          : '';
                      }}
                    >
                      {item.name}
                    </span>
                  </th>
                ))}
                {hideActions == false && (
                  <th className="exclude-export" data-exclude={true}>
                    <span>Actions</span>
                  </th>
                )}
              </tr>
            )}
            {hasGroupedHeaders && groupedHeaderMeta && (
              <>
                <tr className="tg-header" data-header={true}>
                  {enableCheckbox && (
                    <th
                      rowSpan={2}
                      className="tg-cell exclude-export text-center"
                    >
                      <span>Select</span>
                    </th>
                  )}
                  {groupedHeaderMeta.firstRow.map((header, idx) => {
                    if (header.type === 'group') {
                      return (
                        <th
                          key={`tg-group-head-${header.key}-${idx}`}
                          colSpan={header.span}
                          className="tg-cell text-center"
                        >
                          <span>{header.name}</span>
                        </th>
                      );
                    }

                    const item = header.item;
                    return (
                      <th
                        key={`tg-head-row1-${header.index}`}
                        rowSpan={2}
                        className={
                          item.sortable != null && item.sortable
                            ? 'sortable tg-cell'
                            : 'tg-cell'
                        }
                        style={{
                          width: item.width || 'auto',
                          maxWidth: item.maxWidth || 'none',
                          minWidth: item.minWidth || 'auto',
                        }}
                      >
                        <span
                          onClick={() => {
                            item.sortable != null && item.sortable
                              ? sortData(header.index)
                              : '';
                          }}
                        >
                          {item.name}
                        </span>
                      </th>
                    );
                  })}
                  {hideActions == false && (
                    <th
                      rowSpan={2}
                      className="exclude-export"
                      data-exclude={true}
                    >
                      <span>Actions</span>
                    </th>
                  )}
                </tr>
                <tr className="tg-header" data-header={true}>
                  {groupedHeaderMeta.secondRow.map((header) => {
                    const item = header.item;
                    return (
                      <th
                        key={`tg-head-row2-${header.index}`}
                        className={
                          item.sortable != null && item.sortable
                            ? 'sortable tg-cell'
                            : 'tg-cell'
                        }
                        style={{
                          width: item.width || 'auto',
                          maxWidth: item.maxWidth || 'none',
                          minWidth: item.minWidth || 'auto',
                        }}
                      >
                        <span
                          onClick={() => {
                            item.sortable != null && item.sortable
                              ? sortData(header.index)
                              : '';
                          }}
                        >
                          {item.name}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </>
            )}
          </thead>
          <tbody>
            {row.map((item, i) => {
              if (item.isSeparator) {
                return (
                  <tr key={`tg-separator-${i}`} className="tg-separator-row">
                    <td
                      colSpan={
                        col.length +
                        (enableCheckbox ? 1 : 0) +
                        (hideActions ? 0 : 1)
                      }
                      className="tg-separator-cell"
                    >
                      <strong>{item.label}</strong>
                    </td>
                  </tr>
                );
              } else {
                return (
                  <tr
                    key={`tg-row-${i}`}
                    className={`${props.rowModifier ? props.rowModifier(item) : ''} tg-body`}
                  >
                    {enableCheckbox && (
                      <td className="exclude-export tg-cell">
                        <input
                          type="checkbox"
                          checked={selectedList.includes(i)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedList([...selectedList, i]);
                            } else {
                              setSelectedList(
                                selectedList.filter((idx) => idx !== i),
                              );
                            }
                          }}
                        />
                      </td>
                    )}
                    {col.map((data, j) => (
                      <td
                        key={`tg-col-${j}`}
                        className="tg-cell"
                        style={{
                          width: data.width || 'auto',
                          maxWidth: data.maxWidth || 'none',
                          minWidth: data.minWidth || 'auto',
                        }}
                      >
                        <span>
                          {(() => {
                            if (data.key == 'all') {
                              return data.formatter(item);
                            } else if (data.key == 'payment_method') {
                              return data.formatter(item);
                            } else if (data.formatter) {
                              return data.formatter(item[data.key]);
                            } else if (data.type) {
                              switch (data.type) {
                                case 'currency':
                                  return formatCurrency(item[data.key]);
                                case 'date':
                                  return dayjs(item[data.key]).format(
                                    'DD/MM/YYYY',
                                  );
                                case 'datetime':
                                  return dayjs(item[data.key]).format(
                                    'DD/MM/YYYY hh:mm:ss A',
                                  );
                                default:
                                  return item[data.key];
                              }
                            } else {
                              const keys = data.key.split('.');
                              let value = item;
                              for (let i = 0; i < keys.length; i++) {
                                if (!value) break;
                                value = value[keys[i]];
                              }
                              if (value === null || value === undefined) {
                                return '-';
                              } else if (
                                typeof value == 'number' &&
                                !data.noformat
                              ) {
                                return value.toFixed(2);
                              } else {
                                return value;
                              }
                            }
                          })()}
                        </span>
                      </td>
                    ))}
                    {hideActions == false && (
                      <td className="exclude-export" data-exclude={true}>
                        <div className="item-actions">
                          {enabledActions.map((action, i) => (
                            <React.Fragment key={action.action}>
                              {(() => {
                                if (!action.roles.includes(currentRole)) {
                                  return null;
                                }
                                switch (action.action.toLowerCase()) {
                                  case 'edit':
                                    return (
                                      <button
                                        className="actions __edit"
                                        onClick={() => props.onEdit(item)}
                                      >
                                        Edit
                                      </button>
                                    );
                                  case 'delete':
                                    return (
                                      <button
                                        className="actions __delete"
                                        onClick={() => props.onDelete(item)}
                                      >
                                        Delete
                                      </button>
                                    );
                                  case 'invoice':
                                    return (
                                      <button
                                        className="actions __invoice"
                                        onClick={() => generateInvoiceCC(item)}
                                      >
                                        Invoice
                                      </button>
                                    );
                                  case 'memo pdf':
                                    return (
                                      <button
                                        className="actions __pdf"
                                        onClick={() => memoPdf(item)}
                                      >
                                        Invoice
                                      </button>
                                    );
                                  case 'print memo':
                                    return (
                                      <button
                                        className="actions __print-memo"
                                        onClick={() => printMemo(item)}
                                      >
                                        Print memo
                                      </button>
                                    );
                                  case 'pay':
                                    return (
                                      <button
                                        className="actions __pay"
                                        onClick={() => props.onPay(item)}
                                      >
                                        Pay
                                      </button>
                                    );
                                  case 'updatestatus':
                                    return (
                                      <button
                                        className={
                                          item.is_active
                                            ? 'red-btn small-btn'
                                            : 'green-btn small-btn'
                                        }
                                        onClick={() => action.callback(item)}
                                      >
                                        {item.is_active
                                          ? 'Deactivate'
                                          : 'Activate'}
                                      </button>
                                    );
                                  default:
                                    if (
                                      action.activeLoading &&
                                      action.activeLoading.includes(item.uuid)
                                    )
                                      return <Spinner />;
                                    return (
                                      <button
                                        title={action.title || action.action}
                                        className={`actions __${action.icon || action.action.toLowerCase()}`}
                                        onClick={() =>
                                          action.callback
                                            ? action.callback(item)
                                            : console.log(
                                                'No callback provided',
                                              )
                                        }
                                      >
                                        {action.action}
                                      </button>
                                    );
                                }
                              })()}
                            </React.Fragment>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              }
            })}
          </tbody>
        </table>

        {enablePagination === 'enabled' && (
          <Pagination
            handlePagination={props.handlePagination}
            count={props.count}
            limit={props.limit}
            offset={props.offset}
          />
        )}
      </div>
    </>
  );
};

export { export2Excel, export2PDF, formatCurrency, TableGrid };
